import React from 'react';
import { Chip, Stack } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { FeedbackAttachment, FeedbackService } from '../../services/feedback.service';
import { useToast } from '../../context/ToastContext';
import { saveBlob } from '../../utils/saveBlob';

/** "1.2 MB" / "340 KB" for the chip label. */
export const fileSize = (bytes: number): string =>
    bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

interface Props {
    feedbackId: string;
    attachments: FeedbackAttachment[] | undefined;
    /** true on the Kervolt inbox, which downloads through the platform endpoint. */
    platform?: boolean;
}

/** The files sent with a message; clicking one downloads it. */
export const FeedbackAttachmentChips: React.FC<Props> = ({ feedbackId, attachments, platform = false }) => {
    const toast = useToast();
    if (!attachments?.length) return null;

    const download = async (a: FeedbackAttachment) => {
        try {
            saveBlob(await FeedbackService.downloadAttachment(feedbackId, a.id, platform), a.fileName);
        } catch (error) {
            toast.showApiError(error, 'Failed to download the file');
        }
    };

    return (
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }} data-help="feedback.attachments">
            {attachments.map(a => (
                <Chip
                    key={a.id}
                    size="small"
                    variant="outlined"
                    icon={<AttachFileIcon />}
                    label={`${a.fileName} (${fileSize(a.sizeBytes)})`}
                    onClick={() => download(a)}
                    sx={{ maxWidth: 260 }}
                />
            ))}
        </Stack>
    );
};
